import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Card,
  CardContent,
  IconButton,
  List,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  DialogContentText,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Paper,
  LinearProgress,
  Collapse,
  Tooltip,
  Grid,
  CardActions,
  Tabs,
  Tab,
  Divider,
  ListItemIcon
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import AddTaskIcon from '@mui/icons-material/AddTask';
import { format, differenceInDays, addDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { doc, updateDoc, arrayUnion, arrayRemove, getDoc } from 'firebase/firestore';
import { hasReachedLimit } from '../config/planLimits';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { styled } from '@mui/material/styles';
import { CHALLENGE_TEMPLATES } from '../utils/challengeTemplates';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PageLayout from './PageLayout';
import { useTheme, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getContrastText } from '../utils/colorUtils';
import { soundService } from '../services/soundService';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import { usePlan } from '../contexts/PlanContext';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import PlanComparisonDialog from './shared/PlanComparisonDialog';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PlanInfoBanner from './shared/PlanInfoBanner';

const CHALLENGE_CATEGORIES = [
  'Finanças',
  'Saúde',
  'Meditação',
  'Leitura',
  'Exercícios Físicos',
  'Alimentação',
  'Disciplina',
  'Mentalidade',
  'Desenvolvimento Pessoal',
  'Produtividade',
  'Sono',
  'Relacionamentos',
  'Criatividade',
  'Aprendizado',
  'Organização',
].sort();

const PILLARS = [
  { id: 'vitalidade', label: 'Vitalidade (Combustível)' },
  { id: 'mentalidade', label: 'Mentalidade (Controle)' },
  { id: 'prosperidade', label: 'Prosperidade (Alicerce)' }
];

const DEFAULT_PILLAR_COLORS = {
  vitalidade: '#2E7D32',
  mentalidade: '#F9A825',
  prosperidade: '#E65100'
};

const getPillarColor = (pillarId, customColors) => {
  const id = (pillarId === 'expansao' ? 'mentalidade' : (pillarId || 'vitalidade'));
  return customColors?.pillars?.[id] || DEFAULT_PILLAR_COLORS[id] || DEFAULT_PILLAR_COLORS.vitalidade;
};

const getPillarLabel = (pillarId) => {
  const id = (pillarId === 'expansao' ? 'mentalidade' : (pillarId || 'vitalidade'));
  const found = PILLARS.find(p => p.id === id);
  const fullLabel = found ? found.label : PILLARS[0].label;
  return fullLabel.includes(' (') ? fullLabel.split(' (')[0] : fullLabel;
};

///Cards de estatisticas
function StatCard({ icon, title, value, color = 'primary', customColors }) {
  return (
     <Card sx={{ 
       p: { xs: 1, sm: 2 }, 
       height: '100%',
       display: 'flex',
       flexDirection: 'column',
       alignItems: 'center',
       justifyContent: 'center',
       textAlign: 'center',
       borderRadius: 2,
       boxShadow: 2,
       transition: 'transform 0.2s',
       '&:hover': {
         transform: 'translateY(-4px)'
       }
     }}>
       <Box sx={{ 
         color: customColors?.primary || color, 
         mb: 1,
         display: 'flex',
         alignItems: 'center',
         justifyContent: 'center'
       }}>
         {React.cloneElement(icon, { 
           sx: { fontSize: { xs: 32, sm: 40 } }
         })}
       </Box>
       <Typography 
         variant="h6" 
         component="div" 
         gutterBottom 
         sx={{ 
           fontSize: { xs: '0.875rem', sm: '1rem' },
           fontWeight: 'bold',
           lineHeight: 1.2,
           mb: 1,
           display: '-webkit-box',
           WebkitLineClamp: 2,
           WebkitBoxOrient: 'vertical',
           overflow: 'hidden'
         }}
       >
         {title}
       </Typography>
       <Typography 
         variant="h6" 
         sx={{ 
           color: customColors?.primary || 'primary.main',
           fontSize: { xs: '1rem', sm: '1.25rem' },
           fontWeight: 'bold'
         }}
       >
         {value}
       </Typography>
     </Card>
   );
 }

const ExpandButton = styled(IconButton)(({ theme }) => ({
  transition: 'transform 0.3s',
  '&.expanded': {
    transform: 'rotate(180deg)',
  },
}));

export default function Challenges({ customColors, userId, pillarFilter: pillarFilterProp, embeddedInCentral, hideAddButtonInHeader }) {
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [challengeToDelete, setChallengeToDelete] = useState(null);
  const [challengeName, setChallengeName] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [filter, setFilter] = useState('all');
  const [pillarFilter, setPillarFilter] = useState(() => pillarFilterProp || 'all');
  const [editingChallenge, setEditingChallenge] = useState(null);
  const { currentUser } = useAuth();
  const [duration, setDuration] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState('');
  const [endDate, setEndDate] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [dialogStep, setDialogStep] = useState('initial');
  const [newChallenge, setNewChallenge] = useState({
    title: '',
    description: '',
    startDate: '',
    category: '',
    tasks: [],
    pillar: 'vitalidade',
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [soundSettings, setSoundSettings] = useState(null);
  const formRef = useRef(null);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const { PLANS, userPlan, permissions } = usePlan();
  const [showProgressResetDialog, setShowProgressResetDialog] = useState(false);
  const [pendingSave, setPendingSave] = useState(null);
  const [sortBy, setSortBy] = useState('startDateDesc');

  // Método estático para abrir o diálogo de novo desafio
  Challenges.onAdd = () => {
    setSelectedTemplate(null);
    setTasks([]);
    setDuration(7);
    setStartDate(new Date());
    setEditingChallenge(null);
    setOpenDialog(true);
  };

  // Carregar desafios ao montar o componente
  useEffect(() => {
    loadChallenges();
    loadSoundSettings();
  }, []);

  const loadChallenges = async () => {
    if (!currentUser) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setChallenges(userData.challenges || []);
        }
    } catch (error) {
      console.error('Erro ao carregar desafios:', error);
    }
  };

  const loadSoundSettings = async () => {
    if (!currentUser) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.soundSettings) {
          setSoundSettings(userData.soundSettings);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar configurações de som:', error);
    }
  };

  const handleOpen = (challenge = null) => {
    // Verifica se o usuário está no plano básico e já atingiu o limite (apenas para novos desafios)
    if (!challenge && hasReachedLimit(userPlan, 'maxChallenges', challenges.length)) {
      setShowPlanDialog(true);
      return;
    }
    if (challenge) {
      setEditingChallenge(challenge);
      setNewChallenge({
        title: challenge.name || '',
        description: challenge.description || '',
        startDate: challenge.startDate ? format(new Date(challenge.startDate), 'yyyy-MM-dd') : '',
        category: challenge.category || '',
        duration: challenge.duration || '',
        tasks: challenge.tasks || [],
        endDate: challenge.endDate ? format(new Date(challenge.endDate), 'yyyy-MM-dd') : '',
        pillar: challenge.pillar || 'vitalidade',
      });
      setDialogStep('custom');
    } else {
      const defaultPillar =
        pillarFilter && pillarFilter !== 'all' ? pillarFilter : 'vitalidade';
      setEditingChallenge(null);
      setNewChallenge({
        title: '',
        description: '',
        startDate: format(new Date(), 'yyyy-MM-dd'),
        category: '',
        tasks: [],
        duration: '',
        endDate: '',
        pillar: defaultPillar,
      });
      setDialogStep('initial');
    }
    setSelectedTemplate(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewChallenge({
      title: '',
      description: '',
      startDate: format(new Date(), 'yyyy-MM-dd'),
      category: '',
      tasks: [],
      duration: '',
      endDate: '',
      pillar: 'vitalidade',
    });
    setEditingChallenge(null);
    setSelectedTemplate(null);
    setNewTask('');
    setDialogStep('initial');
  };

  const handleAddTask = () => {
    if (newTask.trim()) {
      const newTaskObj = { id: Date.now(), text: newTask.trim(), completed: false };
      setNewChallenge(prev => ({
        ...prev,
        tasks: [...(prev.tasks || []), newTaskObj]
      }));
      setNewTask('');
    }
  };

  const handleToggleTask = (taskId) => {
    setTasks(prev => prev.map(task => 
      task.id === taskId ? { ...task, completed: !task.completed } : task
    ));
  };

  const handleRemoveTask = (taskId) => {
    setTasks(prev => prev.filter(task => task.id !== taskId));
  };

  const handleDurationChange = (value) => {
    setDuration(value);
    if (startDate && value) {
      setEndDate(addDays(new Date(startDate), parseInt(value)));
    }
  };

  const handleStartDateChange = (newValue) => {
    setStartDate(newValue);
    if (newValue && duration) {
      setEndDate(addDays(new Date(newValue), parseInt(duration)));
    }
  };

  const handleSubmit = async () => {
    if (!challengeName || !startDate || !currentUser || !category || !duration) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);

      const pillarForNew = editingChallenge?.pillar || 'vitalidade';

      const challengeData = {
        name: challengeName,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        duration: duration,
        category: category,
        description: description,
        tasks: tasks,
        completed: false,
        pillar: pillarForNew,
      };

      if (editingChallenge) {
        const updatedChallenge = {
          ...editingChallenge,
          ...challengeData,
        };

        await updateDoc(userRef, {
          challenges: arrayRemove(editingChallenge)
        });
        await updateDoc(userRef, {
          challenges: arrayUnion(updatedChallenge)
        });

        setChallenges(prev => 
          prev.map(c => c.id === editingChallenge.id ? updatedChallenge : c)
        );
      } else {
        const newChallenge = {
          id: Date.now().toString(),
          createdAt: new Date().toISOString(),
          ...challengeData,
        };

        await updateDoc(userRef, {
          challenges: arrayUnion(newChallenge)
        });

        setChallenges(prev => [...prev, newChallenge]);
      }

      handleCloseDialog();
    } catch (error) {
      console.error('Erro ao salvar desafio:', error);
    }
  };

  const handleDeleteClick = (challenge) => {
    setChallengeToDelete(challenge);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!challengeToDelete) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);

      if (challengeToDelete.title === 'todos os desafios') {
        // Remove todos os desafios
        await updateDoc(userRef, {
          challenges: []
        });
        setChallenges([]);
      } else {
        // Remove um desafio específico
        await updateDoc(userRef, {
          challenges: arrayRemove(challengeToDelete)
        });
        setChallenges(prev => prev.filter(c => c.id !== challengeToDelete.id));
      }

      setDeleteDialogOpen(false);
      setChallengeToDelete(null);
    } catch (error) {
      console.error('Erro ao deletar desafio(s):', error);
    }
  };

  const handleToggleComplete = async (challenge) => {
    try {
      const wasCompleted = challenge.completed;
      const updatedChallenge = {
        ...challenge,
        completed: !challenge.completed
      };

      const userRef = doc(db, 'users', currentUser.uid);
      
      await updateDoc(userRef, {
        challenges: arrayRemove(challenge)
      });
      await updateDoc(userRef, {
        challenges: arrayUnion(updatedChallenge)
      });

      setChallenges(prev => 
        prev.map(c => c.id === challenge.id ? updatedChallenge : c)
      );

      // Toca o som apenas quando o desafio é concluído (não quando é desmarcado)
      if (!wasCompleted && updatedChallenge.completed) {
        soundService.playChallengeComplete();
      }
    } catch (error) {
      console.error('Erro ao atualizar desafio:', error);
    }
  };

  const getStatusChips = (challenge) => {
    const chips = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(challenge.startDate);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(challenge.endDate);
    endDate.setHours(0, 0, 0, 0);
    const daysUntilStart = differenceInDays(startDate, today);
    const daysUntilEnd = differenceInDays(endDate, today);
    // Etiqueta principal
    if (challenge.completed) {
      chips.push(
        <Chip
          key="concluido"
          label="Concluído"
          color="success"
          size="small"
          sx={{ fontWeight: 'normal' }}
        />
      );
    } else {
      chips.push(
        <Chip
          key="em-andamento"
          label="Em andamento"
          size="small"
          sx={{
            bgcolor: customColors?.primary || 'warning.main',
            color: theme.palette.mode === 'dark'
              ? (customColors?.primary ? theme.palette.primary.contrastText : theme.palette.warning.contrastText)
              : getContrastText(customColors?.primary || '#FFD600'),
            fontWeight: 'normal',
            border: 0
          }}
        />
      );
    }
    // Etiqueta secundária
    if (!challenge.completed) {
      if (daysUntilEnd < 0) {
        chips.push(
          <Chip
            key="em-atraso"
            label={`Em atraso (${Math.abs(daysUntilEnd)} dia${Math.abs(daysUntilEnd) > 1 ? 's' : ''} em atraso)`}
            color="error"
            size="small"
            sx={{ fontWeight: 'normal' }}
          />
        );
      } else {
        chips.push(
          <Chip
            key="no-prazo"
            label={`No prazo (${daysUntilEnd + 1} dia${daysUntilEnd + 1 > 1 ? 's' : ''} restante${daysUntilEnd + 1 > 1 ? 's' : ''})`}
            size="small"
            sx={{
              bgcolor: '#FFD600',
              color: getContrastText('#FFD600'),
              fontWeight: 'normal',
              border: 0
            }}
          />
        );
      }
    }
    return <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>{chips}</Box>;
  };

  const challengesForView = React.useMemo(
    () =>
      pillarFilter && pillarFilter !== 'all'
        ? challenges.filter(c => (c.pillar === 'expansao' ? 'mentalidade' : (c.pillar || 'vitalidade')) === pillarFilter)
        : challenges,
    [challenges, pillarFilter]
  );

  const filteredChallenges = challengesForView.filter(challenge => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(challenge.startDate);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(challenge.endDate);
    endDate.setHours(0, 0, 0, 0);
    const daysUntilStart = differenceInDays(startDate, today);
    const daysUntilEnd = differenceInDays(endDate, today);

    switch (filter) {
      case 'ongoing':
        // Em andamento: não está completo (independente do prazo)
        return !challenge.completed;
      case 'completed':
        return challenge.completed;
      case 'overdue':
        // Em atraso: não está completo e já passou da data de término
        return !challenge.completed && daysUntilEnd < 0;
      case 'onTime':
        // No prazo: não está completo, já começou e ainda não terminou, e está dentro do prazo
        return !challenge.completed && daysUntilEnd >= 0;
      default:
        return true;
    }
  });

  const toggleCardExpand = (challengeId) => {
    setExpandedCards(prev => ({
      ...prev,
      [challengeId]: !prev[challengeId]
    }));
  };

  const handleToggleChallengeTask = async (challenge, taskId) => {
    try {
      const updatedTasks = challenge.tasks.map(task =>
        task.id === taskId ? { ...task, completed: !task.completed } : task
      );

      // Verifica se todas as tarefas foram concluídas
      const allTasksCompleted = updatedTasks.every(task => task.completed);
      const wasCompleted = challenge.completed;

      const updatedChallenge = {
        ...challenge,
        tasks: updatedTasks,
        completed: allTasksCompleted
      };

      const userRef = doc(db, 'users', currentUser.uid);
      
      await updateDoc(userRef, {
        challenges: arrayRemove(challenge)
      });
      await updateDoc(userRef, {
        challenges: arrayUnion(updatedChallenge)
      });

      // Se o desafio foi concluído agora (não estava antes), toca o som
      if (allTasksCompleted && !wasCompleted) {
        soundService.playChallengeComplete();
      }

      setChallenges(prev =>
        prev.map(c => c.id === challenge.id ? updatedChallenge : c)
      );
    } catch (error) {
      console.error('Erro ao atualizar tarefa:', error);
    }
  };

  const calculateProgress = (challenge) => {
    if (challenge.completed) return 100;
    if (!challenge.dailyProgress || challenge.dailyProgress.length === 0) return 0;
    const completedDays = challenge.dailyProgress.filter(day => day.completed).length;
    return Math.round((completedDays / parseInt(challenge.duration)) * 100);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setNewChallenge({
      title: template.name,
      description: template.description,
      startDate: format(new Date(), 'yyyy-MM-dd'),
      category: template.category,
      duration: template.duration,
      tasks: template.tasks.map(task => ({ ...task, id: Date.now() + Math.random() }))
    });

    // Scroll para o formulário após um pequeno delay para garantir que o estado foi atualizado
    setTimeout(() => {
      const dialogContent = document.querySelector('.MuiDialogContent-root');
      if (dialogContent) {
        const scrollHeight = dialogContent.scrollHeight;
        dialogContent.scrollTo({
          top: scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const handleOpenDialog = () => {
    handleOpen();
  };

  const handleSaveChallenge = async () => {
    // Verifica se o usuário está no plano básico e já atingiu o limite (apenas para novos desafios)
    if (!editingChallenge && hasReachedLimit(userPlan, 'maxChallenges', challenges.length)) {
      setShowPlanDialog(true);
      setOpenDialog(false);
      return;
    }

    if (!newChallenge.title.trim()) {
      alert('Por favor, insira um título para o desafio.');
      return;
    }

    if (!newChallenge.startDate) {
      alert('Por favor, selecione uma data de início.');
      return;
    }

    if (!newChallenge.duration || newChallenge.duration <= 0) {
      alert('Por favor, insira uma duração válida.');
      return;
    }

    // Se estiver editando e a data de início foi alterada, pedir confirmação
    if (editingChallenge) {
      const oldStart = editingChallenge.startDate ? format(new Date(editingChallenge.startDate), 'yyyy-MM-dd') : '';
      if (oldStart !== newChallenge.startDate) {
        setPendingSave({ ...newChallenge });
        setShowProgressResetDialog(true);
        return;
      }
    }

    await saveChallengeWithProgress(editingChallenge, newChallenge);
  };

  // Função auxiliar para salvar desafio, com ou sem reset de progresso
  const saveChallengeWithProgress = async (editingChallenge, newChallenge, resetProgress = false) => {
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      let dailyProgress = editingChallenge?.dailyProgress || [];
      let completed = editingChallenge?.completed || false;
      let createdAt = editingChallenge?.createdAt || new Date().toISOString();
      let id = editingChallenge?.id || Date.now().toString();

      // Se for resetar progresso (data de início alterada), gera novo progresso
      if (resetProgress || !editingChallenge) {
        const startDate = new Date(newChallenge.startDate + 'T12:00:00');
        dailyProgress = [];
        for (let i = 0; i < parseInt(newChallenge.duration); i++) {
          const date = addDays(startDate, i);
          dailyProgress.push({
            day: i + 1,
            date: date.toISOString(),
            completed: false
          });
        }
        completed = false;
      }

      const startDate = new Date(newChallenge.startDate + 'T12:00:00');
      const pillarForNew =
        newChallenge.pillar || editingChallenge?.pillar || 'vitalidade';
      const challengeData = {
        id,
        name: newChallenge.title,
        startDate: startDate.toISOString(),
        endDate: addDays(startDate, parseInt(newChallenge.duration) - 1).toISOString(),
        duration: newChallenge.duration,
        category: newChallenge.category,
        description: newChallenge.description || '',
        tasks: newChallenge.tasks || [],
        dailyProgress,
        completed,
        createdAt,
        pillar: pillarForNew,
      };

      if (editingChallenge) {
        await updateDoc(userRef, {
          challenges: arrayRemove(editingChallenge)
        });
      }

      await updateDoc(userRef, {
        challenges: arrayUnion(challengeData)
      });

      setChallenges(prev => {
        if (editingChallenge) {
          return prev.map(c => c.id === editingChallenge.id ? challengeData : c);
        }
        return [...prev, challengeData];
      });

      handleCloseDialog();
    } catch (error) {
      console.error('Erro ao salvar o desafio:', error);
      alert('Erro ao salvar o desafio. Por favor, tente novamente.');
    }
  };

  const handleToggleDayComplete = async (challenge, dayIndex) => {
    try {
      const dailyProgress = [...(challenge.dailyProgress || [])];
      
      // Se não existir progresso diário, cria um array com a duração do desafio
      if (!dailyProgress.length) {
        for (let i = 0; i < parseInt(challenge.duration); i++) {
          const date = addDays(new Date(challenge.startDate), i);
          dailyProgress.push({
            day: i + 1,
            date: date.toISOString(),
            completed: false
          });
        }
      }

      // Verifica se o dia está sendo marcado como completo
      const wasCompleted = dailyProgress[dayIndex].completed;
      const isNowCompleted = !wasCompleted;

      // Atualiza o status do dia
      dailyProgress[dayIndex].completed = isNowCompleted;

      // Verifica se todos os dias foram completados
      const allDaysCompleted = dailyProgress.every(day => day.completed);

      const updatedChallenge = {
        ...challenge,
        dailyProgress,
        completed: allDaysCompleted
      };

      const userRef = doc(db, 'users', currentUser.uid);
      
      // Busca os desafios atuais
      const userDoc = await getDoc(userRef);
      if (userDoc.exists()) {
        const currentChallenges = userDoc.data().challenges || [];
        const updatedChallenges = currentChallenges.map(c => 
          c.id === challenge.id ? updatedChallenge : c
        );

        // Atualiza todos os desafios em uma única operação
        await updateDoc(userRef, {
          challenges: updatedChallenges
        });

        // Toca o som se o dia foi marcado como completo (não quando desmarcado)
        if (isNowCompleted) {
          soundService.playChallengeDayComplete();
        }

        // Se o desafio foi concluído agora, toca o som
        if (allDaysCompleted && !challenge.completed) {
          soundService.playChallengeComplete();
        }

        setChallenges(updatedChallenges);
      }
    } catch (error) {
      console.error('Erro ao atualizar progresso diário:', error);
    }
  };

  // Função para calcular e atualizar o prazo final
  const updateEndDate = (startDate, duration) => {
    if (startDate && duration) {
      // Criar data com horário meio-dia para evitar problemas de fuso horário
      const start = new Date(startDate + 'T12:00:00');
      // Subtrai 1 da duração pois o dia inicial conta como primeiro dia
      const endDate = addDays(start, parseInt(duration) - 1);
      return format(endDate, 'yyyy-MM-dd');
    }
    return '';
  };

  // Atualizar o prazo final quando a data inicial ou duração mudar
  useEffect(() => {
    if (newChallenge.startDate && newChallenge.duration) {
      const calculatedEndDate = updateEndDate(newChallenge.startDate, newChallenge.duration);
      setNewChallenge(prev => ({
        ...prev,
        endDate: calculatedEndDate
      }));
    }
  }, [newChallenge.startDate, newChallenge.duration]);

  const handleDeleteAllConfirm = async () => {
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        challenges: []
      });
      setChallenges([]);
      setShowDeleteAllDialog(false);
    } catch (error) {
      console.error('Erro ao deletar todos os desafios:', error);
    }
  };

  // Função para ordenar desafios conforme seleção
  const sortChallenges = (a, b) => {
    switch (sortBy) {
      case 'startDateAsc':
        return new Date(a.startDate) - new Date(b.startDate);
      case 'startDateDesc':
        return new Date(b.startDate) - new Date(a.startDate);
      case 'endDateAsc':
        return new Date(a.endDate) - new Date(b.endDate);
      case 'endDateDesc':
        return new Date(b.endDate) - new Date(a.endDate);
      default:
        return 0;
    }
  };

  const renderDialogContent = () => {
    if (dialogStep === 'initial') {
      return (
        <>
          <DialogTitle>Criar Novo Desafio</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 2 }}>
              <Card 
                onClick={() => setDialogStep('template')}
                sx={{ 
                  cursor: 'pointer',
                  p: 3,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <AddTaskIcon sx={{ fontSize: '2rem', color: customColors?.primary || 'primary.main' }} />
                  <Typography variant="h6">Usar um Template</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Escolha entre diversos templates pré-definidos com objetivos específicos. 
                  Você pode usar o template exatamente como está ou personalizá-lo de acordo com suas necessidades, 
                  modificando título, descrição, tarefas e duração.
                </Typography>
              </Card>

              <Card 
                onClick={() => setDialogStep('custom')}
                sx={{ 
                  cursor: 'pointer',
                  p: 3,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <EditIcon sx={{ fontSize: '2rem', color: customColors?.primary || 'primary.main' }} />
                  <Typography variant="h6">Criar do Zero</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Crie um desafio totalmente personalizado. Você terá controle total sobre todos os aspectos: 
                  defina seu próprio título, descrição, categoria, duração e crie uma lista de tarefas específica 
                  para seus objetivos.
                </Typography>
              </Card>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
          </DialogActions>
        </>
      );
    }

    return (
      <>
        <DialogTitle>
          {editingChallenge ? 'Editar Desafio' : 'Novo Desafio'}
          {dialogStep === 'template' && !editingChallenge && (
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              Selecione um template ou personalize-o de acordo com suas necessidades
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {dialogStep === 'template' && !editingChallenge && (
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                {CHALLENGE_TEMPLATES.map((template) => (
                  <Grid item xs={12} sm={6} md={4} key={template.id}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4
                        },
                        border: selectedTemplate?.id === template.id ? 2 : 0,
                        borderColor: customColors?.primary || 'primary.main'
                      }}
                      onClick={() => {
                        setNewChallenge({
                          title: template.name,
                          description: template.description,
                          startDate: format(new Date(), 'yyyy-MM-dd'),
                          category: template.category,
                          duration: template.duration,
                          tasks: template.tasks.map(task => ({ ...task, id: Date.now() + Math.random() }))
                        });
                        setSelectedTemplate(template);

                        // Scroll para o formulário após um pequeno delay para garantir que o estado foi atualizado
                        setTimeout(() => {
                          const dialogContent = document.querySelector('.MuiDialogContent-root');
                          if (dialogContent) {
                            const scrollHeight = dialogContent.scrollHeight;
                            dialogContent.scrollTo({
                              top: scrollHeight,
                              behavior: 'smooth'
                            });
                          }
                        }, 100);
                      }}
                    >
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {template.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {template.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip 
                            label={`${template.duration} dias`}
                            size="small"
                            color="primary"
                            sx={{ 
                              bgcolor: customColors?.primary,
                              color: customColors?.primary ? getContrastText(customColors.primary) : theme.palette.primary.contrastText,
                              '&:hover': {
                                bgcolor: customColors?.primary,
                                opacity: 0.9
                              }
                            }}
                          />
                          <Chip 
                            label={template.category}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Divider sx={{ my: 3 }} />
            </Box>
          )}

          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }} ref={formRef}>
            <TextField
              label="Título"
              fullWidth
              value={newChallenge.title}
              onChange={(e) => setNewChallenge(prev => ({ ...prev, title: e.target.value }))}
            />
            <TextField
              label="Descrição"
              fullWidth
              multiline
              rows={2}
              value={newChallenge.description}
              onChange={(e) => setNewChallenge(prev => ({ ...prev, description: e.target.value }))}
            />
            <TextField
              label="Data de Início"
              type="date"
              fullWidth
              value={newChallenge.startDate}
              onChange={(e) => setNewChallenge(prev => ({ ...prev, startDate: e.target.value }))}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  backgroundColor: customColors?.primary || 'primary.main',
                  backgroundImage: 'none',
                  maskImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z\'/%3E%3C/svg%3E")',
                  maskRepeat: 'no-repeat',
                  maskPosition: 'center',
                  maskSize: 'contain',
                  WebkitMaskImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z\'/%3E%3C/svg%3E")',
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                  WebkitMaskSize: 'contain',
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer'
                }
              }}
            />
            <TextField
              label="Duração (dias)"
              type="number"
              fullWidth
              value={newChallenge.duration || ''}
              onChange={(e) => setNewChallenge(prev => ({ ...prev, duration: e.target.value }))}
              InputProps={{ inputProps: { min: 1 } }}
            />
            <TextField
              label="Prazo Final"
              type="date"
              fullWidth
              value={newChallenge.endDate || ''}
              disabled
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  backgroundColor: customColors?.primary || 'primary.main',
                  backgroundImage: 'none',
                  maskImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z\'/%3E%3C/svg%3E")',
                  maskRepeat: 'no-repeat',
                  maskPosition: 'center',
                  maskSize: 'contain',
                  WebkitMaskImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z\'/%3E%3C/svg%3E")',
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                  WebkitMaskSize: 'contain',
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer'
                }
              }}
            />
            <FormControl fullWidth>
              <InputLabel>Pilar</InputLabel>
              <Select
                value={newChallenge.pillar || 'vitalidade'}
                label="Pilar"
                onChange={(e) =>
                  setNewChallenge(prev => ({ ...prev, pillar: e.target.value }))
                }
              >
                {PILLARS.map((pillar) => (
                  <MenuItem key={pillar.id} value={pillar.id}>
                    {getPillarLabel(pillar.id)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Categoria</InputLabel>
              <Select
                value={newChallenge.category}
                label="Categoria"
                onChange={(e) => setNewChallenge(prev => ({ ...prev, category: e.target.value }))}
              >
                {CHALLENGE_CATEGORIES.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Tarefas
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  label="Nova tarefa"
                  fullWidth
                  value={newTask}
                  onChange={(e) => setNewTask(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTask()}
                />
                <Button
                  variant="contained"
                  onClick={handleAddTask}
                  sx={{
                    bgcolor: customColors?.primary || 'primary.main',
                    color: customColors?.primary ? getContrastText(customColors.primary) : theme.palette.primary.contrastText,
                    '&:hover': {
                      bgcolor: customColors?.primary || 'primary.main',
                      opacity: 0.9
                    }
                  }}
                >
                  Adicionar
                </Button>
              </Box>
              <List dense>
                {newChallenge.tasks?.map((task, index) => (
                  <ListItem key={task.id || index}>
                    <ListItemText primary={task.text} />
                    <IconButton onClick={() => {
                      setNewChallenge(prev => ({
                        ...prev,
                        tasks: prev.tasks.filter((_, i) => i !== index)
                      }));
                    }}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          {!editingChallenge && (
            <Button onClick={() => setDialogStep('initial')}>Voltar</Button>
          )}
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleSaveChallenge}
            variant="contained"
            sx={{
              bgcolor: customColors?.primary || 'primary.main',
              color: customColors?.primary ? getContrastText(customColors.primary) : theme.palette.primary.contrastText,
              '&:hover': {
                bgcolor: customColors?.primary || 'primary.main',
                opacity: 0.9
              }
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </>
    );
  };

  const handleAddChallenge = async () => {
    if (permissions && challenges.length >= permissions.maxChallenges) {
      setShowPlanDialog(true);
      return;
    }
    // ... existing code ...
  };

  return (
    <PageLayout
      title="Desafios"
      onAdd={handleOpenDialog}
      customColors={customColors}
      hideLogo={!!embeddedInCentral}
      hideTitle={!!embeddedInCentral}
      hideFooter={!!embeddedInCentral}
      actionButtons={
        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          width: { xs: '100%', sm: 'auto' },
          '& .MuiButton-root': {
            flex: { xs: 1, sm: 'initial' },
            minWidth: { xs: 0, sm: 'auto' },
            px: { xs: 1.5, sm: 3 },
            whiteSpace: 'nowrap',
            fontSize: { xs: '0.75rem', sm: '1rem' }
          }
        }}>
          {!embeddedInCentral && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => {
              setChallengeToDelete({ title: 'todos os desafios' });
              setDeleteDialogOpen(true);
            }}
          >
            Remover Todos
          </Button>
          )}
          {!embeddedInCentral && !hideAddButtonInHeader && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpen()}
              sx={{
                bgcolor: customColors?.primary || 'primary.main',
                color: customColors?.primary ? getContrastText(customColors.primary) : theme.palette.primary.contrastText,
                '&:hover': {
                  bgcolor: customColors?.primary || 'primary.main',
                  opacity: 0.9
                }
              }}
            >
              Novo Desafio
            </Button>
          )}
        </Box>
      }
    >
      {/* Banner informativo do plano */}
      <PlanInfoBanner
        feature="maxChallenges"
        featureName="desafios"
        featureNameSingular="desafio"
        currentCount={challengesForView.length}
        customColors={customColors}
        onUpgradeClick={() => setShowPlanDialog(true)}
      />

      {/* Filtro por pilar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5,
          pl: { xs: 1, sm: 0 }
        }}
      >
        <Button
          size="small"
          variant={pillarFilter === 'all' ? 'contained' : 'outlined'}
          onClick={() => setPillarFilter('all')}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: 999,
            bgcolor: pillarFilter === 'all'
              ? (customColors?.primary || theme.palette.primary.main)
              : 'transparent',
            color: pillarFilter === 'all'
              ? (theme.palette.mode === 'dark' ? theme.palette.primary.contrastText : (customColors?.primary ? getContrastText(customColors.primary) : theme.palette.primary.contrastText))
              : theme.palette.text.primary,
            '&:hover': {
              bgcolor: pillarFilter === 'all'
                ? (customColors?.primary || theme.palette.primary.main)
                : alpha(theme.palette.text.primary, 0.06)
            }
          }}
        >
          Todos
        </Button>
        {PILLARS.map(pillar => {
          const color = getPillarColor(pillar.id, customColors);
          const selected = pillarFilter === pillar.id;
          return (
            <Button
              key={pillar.id}
              size="small"
              variant={selected ? 'contained' : 'outlined'}
              onClick={() => setPillarFilter(pillar.id)}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: 999,
                borderColor: color,
                bgcolor: selected ? color : 'transparent',
                color: selected ? getContrastText(color) : color,
                '&:hover': {
                  bgcolor: selected ? color : alpha(color, 0.08)
                }
              }}
            >
              {getPillarLabel(pillar.id)}
            </Button>
          );
        })}
      </Box>

      {/* Cards de Estatísticas */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard
              icon={<AssignmentIcon />}
              title="Total de Desafios"
              value={challengesForView.length}
              customColors={customColors}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard
              icon={<CheckCircleIcon />}
              title="Desafios Concluídos"
              value={challengesForView.filter(challenge => challenge.completed).length}
              color="success"
              customColors={customColors}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard
              icon={<TrendingUpIcon />}
              title="Em Andamento"
              value={challengesForView.filter(challenge => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const startDate = new Date(challenge.startDate);
                startDate.setHours(0, 0, 0, 0);
                const endDate = new Date(challenge.endDate);
                endDate.setHours(0, 0, 0, 0);
                const daysUntilStart = differenceInDays(startDate, today);
                const daysUntilEnd = differenceInDays(endDate, today);
                return !challenge.completed && daysUntilStart <= 0 && daysUntilEnd >= 0;
              }).length}
              color="info"
              customColors={customColors}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard
              icon={<WarningIcon />}
              title="Em Atraso"
              value={challengesForView.filter(challenge => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const endDate = new Date(challenge.endDate);
                endDate.setHours(0, 0, 0, 0);
                const daysUntilEnd = differenceInDays(endDate, today);
                return !challenge.completed && daysUntilEnd < 0;
              }).length}
              color="error"
              customColors={customColors}
            />
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          mb: 3,
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          gap: { xs: 2, sm: 0 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', width: { xs: '100%', sm: 'auto' } }}>
          <FormControl size="small" fullWidth sx={{ display: { xs: 'block', sm: 'none' } }}>
            <InputLabel>Filtrar</InputLabel>
            <Select
              value={filter}
              label="Filtrar"
              onChange={e => setFilter(e.target.value)}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="ongoing">Em Andamento</MenuItem>
              <MenuItem value="completed">Concluídos</MenuItem>
              <MenuItem value="overdue">Em Atraso</MenuItem>
              <MenuItem value="onTime">No Prazo</MenuItem>
            </Select>
          </FormControl>
          <Tabs
            value={filter}
            onChange={(e, newValue) => setFilter(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              display: { xs: 'none', sm: 'flex' },
              '& .MuiTabs-flexContainer': {
                gap: 1,
                alignItems: 'center'
              },
              '& .MuiTab-root': {
                borderRadius: 1,
                minHeight: 'auto',
                textTransform: 'none',
                fontSize: '0.875rem',
                px: 2,
                py: 1
              },
              '& .MuiTabs-indicator': {
                backgroundColor: customColors?.primary || 'primary.main'
              },
              '& .Mui-selected': {
                color: `${customColors?.primary || 'primary.main'} !important`
              }
            }}
          >
            <Tab label="Todos" value="all" />
            <Tab label="Em Andamento" value="ongoing" />
            <Tab label="Concluídos" value="completed" />
            <Tab label="Em Atraso" value="overdue" />
            <Tab label="No Prazo" value="onTime" />
          </Tabs>
        </Box>
        <FormControl size="small" sx={{ minWidth: 180, ml: { xs: 0, sm: 2 }, mt: { xs: 2, sm: 0 }, alignSelf: { xs: 'flex-start', sm: 'center' } }}>
          <InputLabel>Classificar por:</InputLabel>
          <Select
            value={sortBy}
            label="Classificar por:"
            onChange={e => setSortBy(e.target.value)}
          >
            <MenuItem value="startDateAsc">Data de Início (mais antiga)</MenuItem>
            <MenuItem value="startDateDesc">Data de Início (mais recente)</MenuItem>
            <MenuItem value="endDateAsc">Data de Conclusão (mais antiga)</MenuItem>
            <MenuItem value="endDateDesc">Data de Conclusão (mais recente)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        {[...filteredChallenges]
          .sort(sortChallenges)
          .map((challenge) => {
            const progress = calculateProgress(challenge);
            const pillarColor = getPillarColor(challenge.pillar, customColors);
            const pillarLabel = getPillarLabel(challenge.pillar);

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={challenge.id}>
                <Card sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  borderLeft: `4px solid ${pillarColor}`,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}>
                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6" gutterBottom sx={{ 
                        fontSize: '1rem',
                        color: customColors?.primary || 'primary.main',
                        fontWeight: 'bold',
                        mb: 0,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.2,
                        flexGrow: 1
                      }}>
                        {challenge.name}
                      </Typography>
                    </Box>

                    {getStatusChips(challenge)}
                    
                    <Typography variant="body2" color="text.secondary" sx={{
                      mb: 2,
                      mt: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {challenge.description}
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2" color="text.secondary" fontWeight="">
                        Início: {format(new Date(challenge.startDate), 'dd/MM/yyyy', { locale: ptBR })}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontWeight="">
                        Conclusão: {format(new Date(challenge.endDate), 'dd/MM/yyyy', { locale: ptBR })}
                      </Typography>
                      <Typography variant="body2" color={customColors?.primary || 'primary.main'} fontWeight="bold">
                        Categoria: {challenge.category}
                      </Typography>
                    </Box>

                    <Box sx={{ my: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Tarefas Diárias:
                      </Typography>
                      <List dense sx={{ pl: 2, mb: 2 }}>
                        {challenge.tasks.map((task, index) => (
                          <ListItem 
                            key={index}
                            dense
                            disableGutters
                            sx={{
                              px: 0,
                              '&:before': {
                                content: '"•"',
                                position: 'absolute',
                                left: '-0.8rem',
                              }
                            }}
                          >
                            <ListItemText
                              primary={task.text}
                              primaryTypographyProps={{
                                variant: 'body2',
                                sx: {
                                  fontSize: '0.875rem'
                                }
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>

                      <Typography variant="subtitle2" gutterBottom>
                        Progresso Diário:
                      </Typography>
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
                        gap: 1,
                        mt: 1
                      }}>
                        {(challenge.dailyProgress || []).map((day, index) => (
                          <Box 
                            key={index}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 0.5,
                              border: 1,
                              borderColor: 'divider',
                              borderRadius: 1,
                              p: 0.5
                            }}
                          >
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                lineHeight: 1,
                                fontSize: '0.7rem',
                                fontWeight: 'medium'
                              }}
                            >
                              {`Dia ${String(day.day).padStart(2, '0')}`}
                            </Typography>
                            <Checkbox
                              checked={day.completed}
                              onChange={() => handleToggleDayComplete(challenge, index)}
                              icon={<RadioButtonUncheckedIcon />}
                              checkedIcon={<CheckCircleIcon />}
                              size="small"
                              sx={{
                                p: 0.5,
                                color: customColors?.primary || 'primary.main',
                                '&.Mui-checked': {
                                  color: customColors?.primary || 'primary.main'
                                }
                              }}
                            />
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                lineHeight: 1,
                                fontSize: '0.7rem',
                                color: 'text.secondary'
                              }}
                            >
                              {format(new Date(day.date), 'dd/MM')}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: challenge.completed ? 'success.main' : (customColors?.primary || 'primary.main')
                          }
                        }}
                      />
                      <Typography variant="body2" color="text.secondary" align="right" sx={{ mt: 0.5 }}>
                        {challenge.completed ? '100' : progress}%
                      </Typography>
                    </Box>
                  </CardContent>

                  <Box
                    sx={{
                      px: 1.5,
                      pt: 1,
                      pb: 0.5,
                      borderTop: 0,
                      borderColor: 'divider',
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                  >
                    <Chip
                      size="small"
                      label={pillarLabel}
                      sx={{
                        bgcolor: pillarColor,
                        color: getContrastText(pillarColor),
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }}
                    />
                  </Box>

                  <CardActions sx={{ 
                    justifyContent: 'center',
                    gap: 1,
                    p: 1,
                    borderTop: 1,
                    borderColor: 'divider'
                  }}>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => handleOpen(challenge)}
                        sx={{
                          color: customColors?.primary || 'primary.main'
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(challenge)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
      </Grid>

      {/* Dialog para adicionar/editar desafio */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth={dialogStep === 'initial' ? 'sm' : 'md'} 
        fullWidth
      >
        {renderDialogContent()}
      </Dialog>

      {/* Diálogo de confirmação de exclusão */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            {challengeToDelete?.title === 'todos os desafios'
              ? "Tem certeza que deseja remover todos os desafios? Esta ação não pode ser desfeita."
              : `Tem certeza que deseja excluir o desafio "${challengeToDelete?.name}"? Esta ação não pode ser desfeita.`
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmação para excluir todos os desafios */}
      <Dialog
        open={showDeleteAllDialog}
        onClose={() => setShowDeleteAllDialog(false)}
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir todos os desafios? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteAllDialog(false)}>Cancelar</Button>
          <Button onClick={handleDeleteAllConfirm} color="error" variant="contained">
            Excluir Todos
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de comparação de planos */}
      <PlanComparisonDialog
        open={showPlanDialog}
        onClose={() => setShowPlanDialog(false)}
        customColors={customColors}
      />

      {/* Diálogo de confirmação para resetar progresso ao alterar data de início */}
      <Dialog
        open={showProgressResetDialog}
        onClose={() => setShowProgressResetDialog(false)}
      >
        <DialogTitle>Atenção</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ao alterar a data de início do desafio, todo o progresso diário será apagado e reiniciado. Tem certeza que deseja continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowProgressResetDialog(false); setPendingSave(null); }}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={async () => {
            setShowProgressResetDialog(false);
            await saveChallengeWithProgress(editingChallenge, pendingSave, true);
            setPendingSave(null);
          }}>Continuar e apagar progresso</Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
} 